import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useEffect, useMemo, useState } from 'react';
import { useGetBenchmarkListQuery } from 'services/newApi/benchmarkApi';
import { useGetFundsListQuery } from 'services/newApi/fundsApi';
import { useGetValuationDatesByContextQuery } from 'services/newApi/valuationDatesApi';
import { useGetContextsQuery } from 'services/newApi/contextsApi';
import { useActions, useAppSelector } from 'state/store';
import { validateToken } from '@ihsm/ui-shared';

type UseGetServiceBootstrapType = {
  valuationDates: Date[] | undefined;
  fundsList: string[];
  benchmarkList: string[];
  contextsList: string[];
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
};

export const useGetServiceBootstrap = (): UseGetServiceBootstrapType => {
  const [skip, setSkip] = useState<boolean>(true);
  const [skipValuationDatesApi, setSkipValuationDatesApi] =
    useState<boolean>(true);
  const [skipContexts, setSkipContexts] = useState<boolean>(true);
  const { setShowNoDataView } = useActions();
  const { selectedValuationDate } = useAppSelector(
    (state) => state.valuationDatesState
  );
  const { loginRequired, token } = useAppSelector((state) => state.authState);
  const { selectedContext } = useAppSelector((state) => state.contextsState);

  const {
    data: contextsApiResponse,
    error: contextsErr,
    isLoading: isContextsLoading,
    isSuccess: isContextsSuccess,
  } = useGetContextsQuery(undefined, { skip: skipContexts });

  const {
    data: valuationDatesApiResponse,
    error: valuationErr,
    isLoading: isValuationDatesLoading,
    isSuccess: isValuationDatesSuccess,
  } = useGetValuationDatesByContextQuery(selectedContext, {
    skip: skipValuationDatesApi,
  });

  const {
    data: fundsListApiResponse,
    error: fundsListErr,
    isLoading: isFundsLoading,
  } = useGetFundsListQuery(selectedValuationDate, {
    skip,
    selectFromResult: ({ data, ...args }) => ({
      data: data?.map((fund) => fund.name),
      ...args,
    }),
  });

  const {
    data: benchmarkListApiResponse,
    error: benchmarkListErr,
    isLoading: isBenchmarkLoading,
  } = useGetBenchmarkListQuery(selectedValuationDate, {
    skip,
    selectFromResult: ({ data, ...args }) => ({
      data: data?.map((fund) => fund.name),
      ...args,
    }),
  });

  const valuationDates = useMemo(
    () =>
      valuationDatesApiResponse?.map((data) => new Date(data.valuation_date)),
    [valuationDatesApiResponse]
  );

  const contextList = useMemo(
    () => contextsApiResponse?.map((data) => data.context),
    [contextsApiResponse]
  );

  useEffect(() => {
    if (
      isContextsSuccess &&
      contextsApiResponse &&
      contextsApiResponse?.length
    ) {
      setSkipValuationDatesApi(false);
    }
  }, [isContextsSuccess, contextsApiResponse]);

  useEffect(() => {
    if (loginRequired) {
      setSkipContexts(!validateToken());
    } else {
      setSkipContexts(false);
    }
  }, [loginRequired, token]);

  useEffect(() => {
    if (
      isValuationDatesSuccess &&
      valuationDatesApiResponse &&
      valuationDatesApiResponse?.length
    ) {
      setSkip(false);
    }
  }, [isValuationDatesSuccess, valuationDatesApiResponse]);

  useEffect(() => {
    if (valuationErr || fundsListErr || benchmarkListErr) {
      setShowNoDataView(true);
    }
  }, [benchmarkListErr, fundsListErr, setShowNoDataView, valuationErr]);

  return {
    valuationDates,
    fundsList: fundsListApiResponse || [],
    benchmarkList: benchmarkListApiResponse || [],
    contextsList: contextList || [],
    isLoading:
      isValuationDatesLoading ||
      isFundsLoading ||
      isBenchmarkLoading ||
      isContextsLoading,
    error: valuationErr || fundsListErr || benchmarkListErr || contextsErr,
  };
};
